import { FieldPath } from "react-hook-form";

import usePaginationFromEndpoint, {
    usePaginationFromEndpointProps,
} from "@/components/templates/PaginatedList/usePaginationFromEndpoint";
import useSearchFromEndpoint, {
    useSearchFromEndpointProps,
} from "@/components/templates/SearchList/useSearchFromEndpoint";
import { documentApi } from "@/shared/apis";
import {
    RelatedDocumentsQueryParameters,
    searchableDocumentMapper,
    SearchDocumentsQueryParameters,
} from "@/shared/apis/documentApi";
import {
    Document,
    DocumentPagination,
    RelatedDocumentsSortProperty,
    SearchableDocument,
    SearchDocumentsSortProperty,
    SortOrder,
} from "@/shared/domain";
import { usePolicyholders } from "@/shared/hooks/usePolicyholders";

type SearchQueryProps = {
    textSearch: string;
    sortProperty: string;
    filter?: MyBrokerApi.DocumentFilter;
};

interface SearchRelatedQueryProps extends SearchQueryProps {
    id: string;
}

export const documentSearchFilters: FieldPath<SearchableDocument>[] = [
    "filename",
    "description",
    "secondaryDescription",
    "formattedDate",
    "status.name",
    "formattedTypeSize",
];

type useDocumentQueriesProps = {
    tableMode: boolean;
    policyholders: number[];
    useDocumentsPageQuery: (
        filter: MyBrokerApi.DocumentFilter,
    ) => usePaginationFromEndpointProps<Document, SearchDocumentsSortProperty>;
    useDocumentsSearchQuery: (searchOptions: SearchQueryProps) => useSearchFromEndpointProps<Document>;
    useDocumentsRelatedPageQuery: (
        id: string,
        filter: MyBrokerApi.DocumentFilter,
    ) => usePaginationFromEndpointProps<Document, RelatedDocumentsSortProperty>;
    useDocumentsRelatedSearchQuery: (searchOptions: SearchRelatedQueryProps) => useSearchFromEndpointProps<Document>;
};

const getRelatedDocumentsById =
    (id: string) =>
    async (requestParams: RelatedDocumentsQueryParameters = {}) =>
        documentApi.getRelatedDocuments(id, requestParams);

const useDocumentQueries = (): useDocumentQueriesProps => {
    const { policyholders } = usePolicyholders();
    const tableMode = true;

    const useDocumentsPageQuery = (filter: MyBrokerApi.DocumentFilter = "all") => {
        return usePaginationFromEndpoint<
            Document,
            DocumentPagination,
            SearchDocumentsQueryParameters,
            SearchDocumentsSortProperty
        >({
            objectType: "document",
            tableMode,
            policyholdersIdList: policyholders,
            otherParams: { filter },
            endPoint: documentApi.getDocuments,
            defaultSorting: { property: "date", order: SortOrder.Descending },
        });
    };

    const useDocumentsSearchQuery = ({ textSearch, sortProperty, filter = "all" }: SearchQueryProps) => {
        const filterDocuments = (results: Document[]) => {
            return searchableDocumentMapper(results).filterBy(textSearch, documentSearchFilters).sortBy(sortProperty);
        };
        return useSearchFromEndpoint<Document, DocumentPagination, SearchDocumentsQueryParameters>(
            "documents",
            documentApi.getDocuments,
            policyholders,
            filterDocuments,
            { filter },
        );
    };

    const useDocumentsRelatedPageQuery = (id: string, filter: MyBrokerApi.DocumentFilter = "all") => {
        return usePaginationFromEndpoint<
            Document,
            DocumentPagination,
            RelatedDocumentsQueryParameters,
            RelatedDocumentsSortProperty
        >({
            id,
            tableMode,
            objectType: "document",
            policyholdersIdList: policyholders,
            otherParams: { filter },
            endPoint: getRelatedDocumentsById(id),
            defaultSorting: { property: "date", order: SortOrder.Descending },
        });
    };

    const useDocumentsRelatedSearchQuery = ({
        textSearch,
        sortProperty,
        id,
        filter = "all",
    }: SearchRelatedQueryProps) => {
        const filterDocuments = (results: Document[]) => {
            return searchableDocumentMapper(results).filterBy(textSearch, documentSearchFilters).sortBy(sortProperty);
        };
        return useSearchFromEndpoint<Document, DocumentPagination, RelatedDocumentsQueryParameters>(
            `${id}documents`,
            getRelatedDocumentsById(id),
            policyholders,
            filterDocuments,
            { filter },
        );
    };

    return {
        tableMode,
        policyholders,
        useDocumentsPageQuery,
        useDocumentsSearchQuery,
        useDocumentsRelatedPageQuery,
        useDocumentsRelatedSearchQuery,
    };
};

export default useDocumentQueries;
